// Enums
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  DIRECTOR = 'director',
  UTP = 'utp',
  TEACHER = 'teacher',
  GUARDIAN = 'guardian',
  STUDENT = 'student',
}

export enum AttendanceStatus {
  PRESENT = 'present',
  ABSENT = 'absent',
  LATE = 'late',
  JUSTIFIED = 'justified',
}

export enum JustificationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum LevelType {
  BASICA = 'basica',
  MEDIA = 'media',
}

// Base interfaces
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export interface TenantBaseEntity extends BaseEntity {
  tenantId: string;
}

// Auth
export interface LoginRequest {
  email: string;
  password: string;
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string | number;
  user: User;
}

// User
export interface User extends TenantBaseEntity {
  email: string;
  firstName: string;
  lastName: string;
  rut: string;
  role: UserRole;
  phone?: string;
  isActive: boolean;
}

// Tenant
export interface Tenant extends BaseEntity {
  name: string;
  rbd: string;
  address?: string;
  comuna?: string;
  region?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
  geoLatitude?: number;
  geoLongitude?: number;
  geoRadiusMeters?: number;
  geoValidationEnabled?: boolean;
  subscriptionPlan?: string;
  isActive: boolean;
}

// Academic
export interface AcademicYear extends TenantBaseEntity {
  year: number;
  name: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

export interface Level extends TenantBaseEntity {
  name: string;
  code: string;
  type: LevelType;
  order: number;
  isActive: boolean;
}

export interface Grade extends TenantBaseEntity {
  name: string;
  code: string;
  levelId: string;
  level?: Level;
  order: number;
  isActive: boolean;
}

export interface Subject extends TenantBaseEntity {
  name: string;
  code: string;
  isActive: boolean;
}

export interface Course extends TenantBaseEntity {
  name: string;
  code: string;
  section?: string;
  classroom?: string;
  gradeId: string;
  grade?: Grade;
  academicYearId: string;
  academicYear?: AcademicYear;
  isActive: boolean;
  studentCount?: number;
  teacherCount?: number;
}

// Course Relations
export interface CourseStudent extends TenantBaseEntity {
  courseId: string;
  course?: Course;
  studentId: string;
  student?: User;
  numeroLista?: number;
  enrollmentDate: string;
  isActive: boolean;
}

export interface CourseTeacher extends TenantBaseEntity {
  courseId: string;
  course?: Course;
  teacherId: string;
  teacher?: User;
  isHeadTeacher: boolean;
  isActive: boolean;
}

export interface CourseSubject extends TenantBaseEntity {
  courseId: string;
  course?: Course;
  subjectId: string;
  subject?: Subject;
  teacherId: string;
  teacher?: User;
  scheduleInfo?: Record<string, any>;
  isActive: boolean;
}

// Attendance
export interface DailyAttendance extends TenantBaseEntity {
  studentId: string;
  student?: User;
  courseId: string;
  course?: Course;
  date: string;
  status: AttendanceStatus;
  arrivalTime?: string;
  observations?: string;
  notes?: string; // alias for observations
  registeredById: string;
  registeredBy?: User;
  numeroLista?: number;
  attendanceId?: string;
}

export interface SubjectAttendance extends TenantBaseEntity {
  studentId: string;
  student?: User;
  courseSubjectId: string;
  courseSubject?: CourseSubject;
  date: string;
  blockNumber: number;
  status: AttendanceStatus;
  arrivalTime?: string;
  observations?: string;
  notes?: string; // alias for observations
  latitude?: number;
  longitude?: number;
  isWithinGeofence?: boolean;
  registeredById: string;
  registeredBy?: User;
}

export interface Justification extends TenantBaseEntity {
  studentId: string;
  student?: User;
  startDate: string;
  endDate: string;
  reason: string;
  documentUrl?: string;
  status: JustificationStatus;
  requestedById: string;
  requestedBy?: User;
  reviewedById?: string;
  reviewedBy?: User;
  reviewedAt?: string;
  reviewNotes?: string;
}

// Student Guardian
export interface StudentGuardian extends TenantBaseEntity {
  studentId: string;
  student?: User;
  guardianId: string;
  guardian?: User;
  relationship: string;
  isPrimary: boolean;
  canViewAttendance: boolean;
  canRequestJustification: boolean;
}

// SIGE Data
export interface StudentSigeData extends TenantBaseEntity {
  studentId: string;
  student?: User;
  sigeId?: string;
  birthDate?: string;
  gender?: string;
  nationality?: string;
  address?: string;
  commune?: string;
  region?: string;
  healthInsurance?: string;
  schoolInsurance?: boolean;
  priorityStudent?: boolean;
  pieStudent?: boolean;
}

// DTOs para requests
export interface CreateAttendanceDto {
  courseId: string;
  date: string;
  records: AttendanceRecordInput[];
}

export interface AttendanceRecordInput {
  studentId: string;
  status: AttendanceStatus;
  observations?: string;
}

export interface CreateSubjectAttendanceDto {
  courseSubjectId: string;
  date: string;
  blockNumber: number;
  records: AttendanceRecordInput[];
}

export interface CreateJustificationDto {
  studentId: string;
  startDate: string;
  endDate: string;
  reason: string;
  documentUrl?: string;
}

// Pagination
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

// Dashboard stats
export interface DashboardStats {
  totalStudents: number;
  presentToday: number;
  absentToday: number;
  lateToday: number;
  attendancePercentage: number;
  weeklyTrend: WeeklyTrendItem[];
  courseStats: CourseAttendanceStat[];
}

export interface WeeklyTrendItem {
  date: string;
  percentage: number;
}

export interface CourseAttendanceStat {
  courseId: string;
  courseName: string;
  totalStudents: number;
  presentCount: number;
  percentage: number;
}

// Reports
export interface MonthlyReport {
  studentId: string;
  studentName: string;
  studentRut: string;
  courseName: string;
  totalDays: number;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  justifiedDays: number;
  attendancePercentage: number;
}

// Menu item for navigation
export interface MenuItem {
  title: string;
  icon: string;
  to?: string;
  children?: MenuItem[];
  roles?: UserRole[];
}
